<script setup lang="ts">
import type * as pf from '@amirada/lib-shared'
import {
  ut_getText,
  ut_getValuesByPath, ut_isEmbedded,
  ut_isScreenSize,
  ut_navigateNewRoute,
  ut_returnNewRoute,
} from '@base/utils/utils'
import { EScreenSize } from '@base/utils/constants'
import { useAppIndexStore } from '@base/stores/indexStore'
import { useDetail } from '@base/composables/detail'
import type { TabsItem } from '#ui/types'
import type * as tp from '@base/utils/types'

const p = defineProps<{
  realCountry: string
  countryLanguage: string
  isHistoric: boolean
  isNavigation: boolean
  isApplication: boolean
  isProducts: boolean
  isAccessories: boolean
  isConfig: boolean
  isInternal: boolean
  isSeries: boolean
  isMotors?: boolean
  showKoreanTeaser?: boolean
}>()

const indexStore = useAppIndexStore()
// No objPath: this reads the node the navigation is on. Handing it paramData.namePath would look
// like the same thing but freeze the path at setup — and this component is not remounted when one
// configuration is left for the next, so it would keep serving the previous one's data.
const cpDetail = useDetail({})

// The third tab is not available for Japan
const hasAccessoriesTab: boolean = indexStore.state.paramData.rcco !== 'jp'

const tabItems: TabsItem[] = [{
  value: '8ae2819c8e9774cf018ea36fa2c86c6b',
  label: ut_getText('tab_8ae2819c8e9774cf018ea36fa2c86c6b'),
  icon2: 'i-wilo-wilo-pump',
}, {
  value: '8ae2819c8e9774cf018ea36fd8117179',
  label: ut_getText('tab_8ae2819c8e9774cf018ea36fd8117179'),
  icon2: 'i-wilo-house-building-solid-full',
}, ...(hasAccessoriesTab
  ? [{
      value: '8ae2819c8e9774cf018ea370051d1130',
      label: ut_getText('tab_8ae2819c8e9774cf018ea370051d1130'),
      icon2: 'i-wilo-hexagon-plus-sharp-solid-full',
    }]
  : []),
]

function getHeaderPic(): string {
  let res: string = ''
  const fallBack: string = 'https://cms.media.wilo.com/dcipicpfinder/wilo523454/6347594/wilo523454_1.jpg'

  if (p.isAccessories) {
    res = 'https://cms.media.wilo.com/dcipicpfinder/wilo523439/6349078/wilo523439_1.jpg'
  }
  else if (p.isNavigation) {
    res = ut_getValuesByPath(indexStore.state.currentNode, 'graphic_data_aerial_view')
    if (!res) { res = fallBack }
  }
  else if (p.isConfig) {
    const detailData = cpDetail.detailData.value
    if (detailData && detailData.nodes) {
      res = ut_getValuesByPath(detailData, 'range_data/graphic_data_aerial_view')
      if (!res) { res = fallBack }
    }
  }
  return res
}

function getHeaderText(type: number): string {
  const currentNode: pf.TPfNode = indexStore.state.currentNode
  let res: string
  if (type === 1) {
    res = getALText('8ae2819c8fb7d8dc018fb87ade876c8a') // Promotional headline if avalable
    if (!res) { res = currentNode.desc }
  }
  else {
    res = getALText('8ae2819c60d3f11d0160e55edb052112') // Promotional subline
  }
  return res
}

function getALText(propName: string): string {
  let res: string = ''
  if (p.isNavigation && p.isApplication) {
    res = ut_getValuesByPath(indexStore.state.currentNode, propName)
  }
  else {
    const detailData = cpDetail.detailData.value
    if (detailData) {
      res = ut_getValuesByPath(detailData, 'range_data/' + propName)
    }
  }
  return res
}

function getNewRoute(name: string): tp.IParamRouter {
  return ut_returnNewRoute([indexStore.getCountryLangPath, 'root13', name].join('/'), { rawurl: '1' }, '', { noPageParams: true, noPsx: true, noTab: true })
}

function getIcon(value: string): string {
  let res: string = ''
  const item = tabItems.find(i => i.value === value)
  if (item) {
    res = item.icon2
  }
  return res
}
</script>

<template>
  <!-- Header Pic and headlines -->
  <div v-if="isProducts || isAccessories || isApplication" class="mb-12">
    <div class="hidden md:block relative">
      <Image
        :src="getHeaderPic()"
        :alt="indexStore.state.currentNode.desc"
        custom-class="h-[450px] object-cover w-full"
        :no-lazy="true"
      />
      <div class="absolute top-0 h-[450px] w-2/3 bg-linear-to-r from-gunmetal to-transparent opacity-75" />
      <div class="absolute top-0 h-[450px] w-full">
        <UContainer class="relative text-white mt-20">
          <h1 class="text-white css-xl font-normal pb-4" v-html="getHeaderText(1)" />
          <div class="text-white css-2xl max-w-[800px]" v-html="getHeaderText(2)" />
        </UContainer>
      </div>
    </div>

    <!-- Tabs Desktop view -->
    <div v-if="!ut_isScreenSize([EScreenSize.sc_xs, EScreenSize.sc_sm])" class="hidden md:block relative">
      <div class="absolute top-[106px] left-0 w-full h-[6px] bg-gunmetal-4" />
      <UContainer>
        <UTabs
          class="-mt-6 group"
          :items="tabItems"
          :default-value="!isApplication ? indexStore.state.currentNode.nodeName : tabItems[1]?.value"
          :unmount-on-hide="false"
          :ui="{ trigger: 'data-[state=active]:bg-white h-[98px]', label: 'h-full' }"
        >
          <template #default="{ item }">
            <NuxtLink :to="getNewRoute((item.value || '').toString())" @click="indexStore.deleteScrollPos()">
              <div class="h-full flex">
                <div class="mt-auto mb-3 flex items-center">
                  <div class="bg-gunmetal-2 group-data-[state=active]:bg-primary p-2 rounded-lg mr-3 flex items-center">
                    <UIcon :name="item.icon2" class="text-white size-6" />
                  </div>
                  <div v-html="item.label" />
                </div>
              </div>
            </NuxtLink>
          </template>
        </UTabs>
      </UContainer>
    </div>

    <!-- Select Mobile view -->
    <div v-else class="block md:hidden">
      <UContainer>
        <USelect
          class="w-full"
          :default-value="!isApplication ? indexStore.state.currentNode.nodeName : tabItems[1]?.value"
          :items="tabItems"
          :ui="{
            base: 'text-xl text-primary font-bold ps-16 py-4',
            itemLabel: 'text-xl font-bold',
            item: 'items-center py-4 data-highlighted:bg-gunmetal-4 data-highlighted:cursor-pointer',
          }"
          @update:model-value="ut_navigateNewRoute([indexStore.getCountryLangPath, 'root13', $event].join('/'), { rawurl: '1' }, '', true)"
          @click="indexStore.deleteScrollPos()"
        >
          <template #item-leading="{ item }">
            <div class="bg-gunmetal-2 p-2 rounded-lg mr-2 flex items-center">
              <!-- @vue-ignore icon2 is not typed -->
              <UIcon
                v-if="item"
                :name="item.icon2"
                class="text-white size-6"
              />
            </div>

          </template>
          <template #leading="{ modelValue }">
            <div class="bg-primary p-2 rounded-lg flex items-center">
              <UIcon
                v-if="modelValue"
                :name="getIcon(modelValue.toString())"
                class=" text-white size-6"
              />
            </div>
          </template>
        </USelect>
        <h1 class="pt-8" v-html="getHeaderText(1)" />
      </UContainer>
    </div>
  </div>

  <!-- Navigation cards if is Application Landscape -->
  <div v-if="isNavigation" class="mt-13">
    <LazyNavigation
      :is-application="true"
      :is-internal="false"
      :real-country="realCountry"
      :show-korean-teaser="showKoreanTeaser"
    />
  </div>

  <!-- Series Configs if is not Application Landscape -->
  <UContainer v-else>

    <div v-if="isHistoric" class="mb-12">
      <h1 v-html="indexStore.state.currentNode.desc" />
      <div class="bg-gunmetal p-8 rounded-lg mt-12 flex justify-center">
        <LazySearchInput :is-historic="true" :use-adesso="ut_isEmbedded()" :real-country="realCountry" />
      </div>
    </div>

    <!-- Catalog results (mask + selected filters + list cards) — shared with the price-list catalog modal -->
    <CatalogView
      :real-country="realCountry"
      :country-language="countryLanguage"
      :is-historic="isHistoric"
      :is-internal="isInternal"
      :is-series="isSeries"
      :is-application="isApplication"
      :is-motors="isMotors"
      :show-korean-teaser="showKoreanTeaser"
    />
  </UContainer>

  <!-- Application info texts -->
  <!-- Temporary disabled -->
  <!--  <UContainer v-if="isApplication" class="css-description mt-12">
    <div v-if="getALText('8ae2819c5ffba3ca01600246ccbd37ca')" class="mb-4" v-html="getALText('8ae2819c5ffba3ca01600246ccbd37ca')" />  &lt;!&ndash; desc_promotext_short &ndash;&gt;
    <div v-if="getALText('8ae2819c5eb84e94015ec7938a8d498a')" v-html="getALText('8ae2819c5eb84e94015ec7938a8d498a')" />  &lt;!&ndash; desc_promotext &ndash;&gt;
    <div v-if="getALText('8ae2889e57d096e70157d1eefa50447a')" v-html="getALText('8ae2889e57d096e70157d1eefa50447a')" />  &lt;!&ndash; desc_7_advantages &ndash;&gt;
  </UContainer> -->
</template>
